import { useEffect, useMemo, useRef, useState, Fragment, type ReactNode } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { useAuth, ROLE_ADMIN, ROLE_CALCULATOR, ROLE_COLLABORATOR_ADMIN } from "@/contexts/AuthContext";
import { useCollaboratorsRealtimeQuery } from "@/features/Collaborators/Data";
import type { Collaborator } from "@/features/Collaborators/Models/collaborator";
import { normalizeString } from "@/utils/stringUtils";

import { DEFAULT_CALCULATOR_PARAMS } from "../../constants";
import type { CalculatorInput, CalculatorResult } from "../../models";
import { calculatePricing, CalculatorError, toCltEquivalentSalary } from "../../utils";
import { formatCurrency, formatPercent, parseNumber } from "../../utils/formatting";

export type TeamCalculatorCardValues = {
  bySalary: boolean;
  bySaleHourlyRate: boolean;
  bySpread: boolean;
  salary: string;
  saleHourlyRate: string;
  spreadRate: string;
  hoursQuantity: string;
  hoursUnit: "hours" | "weeks" | "months";
};

export const defaultTeamCalculatorCardValues: TeamCalculatorCardValues = {
  bySalary: false,
  bySaleHourlyRate: false,
  bySpread: false,
  salary: "",
  saleHourlyRate: "",
  spreadRate: DEFAULT_CALCULATOR_PARAMS.spreadRate.toString(),
  hoursQuantity: "168",
  hoursUnit: "hours",
};

export type TeamCalculatorCardSnapshot = {
  id: string;
  title: string;
  values: TeamCalculatorCardValues;
  result: CalculatorResult | null;
};

type TeamCalculatorCardProps = {
  cardId: string;
  index: number;
  canRemove: boolean;
  onRemove?: () => void;
  onSnapshotChange?: (cardId: string, snapshot: TeamCalculatorCardSnapshot) => void;
  initialSnapshot?: TeamCalculatorCardSnapshot;
  isHoursModuleEnabled?: boolean;
};

export function TeamCalculatorCard({
  cardId,
  index,
  canRemove,
  onRemove,
  onSnapshotChange,
  initialSnapshot,
  isHoursModuleEnabled = false,
}: TeamCalculatorCardProps) {
  const { userHasRole } = useAuth();
  const canSelectCollaborator =
    userHasRole(ROLE_ADMIN) ||
    (userHasRole(ROLE_CALCULATOR) && userHasRole(ROLE_COLLABORATOR_ADMIN));
  const defaultTitle = useMemo(() => `Profissional ${index + 1}`, [index]);
  const [values, setValues] = useState<TeamCalculatorCardValues>(() =>
    initialSnapshot?.values ? { ...initialSnapshot.values } : { ...defaultTeamCalculatorCardValues },
  );
  const [title, setTitle] = useState(initialSnapshot?.title ?? defaultTitle);
  const [selectedCollaborator, setSelectedCollaborator] = useState<CollaboratorOption | null>(null);
  const [isSelectingCollaborator, setIsSelectingCollaborator] = useState(false);
  const previousDefaultTitle = useRef(defaultTitle);
  const hydratedSnapshotId = useRef<string | null>(initialSnapshot?.id ?? null);

  useEffect(() => {
    if (title === previousDefaultTitle.current) {
      setTitle(defaultTitle);
    }
    previousDefaultTitle.current = defaultTitle;
  }, [defaultTitle, title]);

  useEffect(() => {
    if (initialSnapshot && initialSnapshot.id !== hydratedSnapshotId.current) {
      setTitle(initialSnapshot.title);
      setValues({ ...initialSnapshot.values });
      hydratedSnapshotId.current = initialSnapshot.id;
    }
  }, [initialSnapshot]);

  const calculateMonthlyHours = (quantity: string, unit: string) => {
    const qty = parseNumber(quantity) ?? 0;
    switch (unit) {
      case "weeks":
        return qty * 42;
      case "months":
        return qty * 168;
      default:
        return qty;
    }
  };

  const selectionCount = useMemo(() => {
    return [values.bySalary, values.bySaleHourlyRate, values.bySpread].filter(Boolean).length;
  }, [values.bySalary, values.bySaleHourlyRate, values.bySpread]);

  const driverDisabled = {
    bySalary: selectionCount >= 2 && !values.bySalary,
    bySaleHourlyRate: selectionCount >= 2 && !values.bySaleHourlyRate,
    bySpread: selectionCount >= 2 && !values.bySpread,
  };

  const { result, errorMessage } = useMemo(() => {
    if (selectionCount !== 2) {
      return {
        result: null,
        errorMessage: "Selecione exatamente dois parâmetros para calcular o restante automaticamente.",
      };
    }

    if (values.bySalary && !parseNumber(values.salary)) {
      return {
        result: null,
        errorMessage: "Informe o salário para usar este parâmetro.",
      };
    }

    if (values.bySaleHourlyRate && !parseNumber(values.saleHourlyRate)) {
      return {
        result: null,
        errorMessage: "Informe o valor da hora de venda.",
      };
    }

    if (values.bySpread && !parseNumber(values.spreadRate)) {
      return {
        result: null,
        errorMessage: "Informe o valor do spread.",
      };
    }

    const profitSharing = DEFAULT_CALCULATOR_PARAMS.profitSharing;
    const monthlyHours = isHoursModuleEnabled
      ? calculateMonthlyHours(values.hoursQuantity, values.hoursUnit)
      : DEFAULT_CALCULATOR_PARAMS.monthlyHours;

    const ratio = isHoursModuleEnabled
      ? monthlyHours / DEFAULT_CALCULATOR_PARAMS.monthlyHours
      : 1;

    const rawSalary = parseNumber(values.salary);
    const adjustedSalary =
      rawSalary && isHoursModuleEnabled
        ? rawSalary * ratio
        : rawSalary;

    const input: CalculatorInput = {
      bySalary: values.bySalary,
      bySaleHourlyRate: values.bySaleHourlyRate,
      bySpread: values.bySpread,
      clt: true,
      salary: adjustedSalary,
      saleHourlyRate: parseNumber(values.saleHourlyRate),
      spreadRate: parseNumber(values.spreadRate) ?? DEFAULT_CALCULATOR_PARAMS.spreadRate,
      taxesRate: DEFAULT_CALCULATOR_PARAMS.taxesRate,
      profitSharing: profitSharing,
      monthlyHours: monthlyHours,
      inssRate: DEFAULT_CALCULATOR_PARAMS.inssRate,
      fgtsRate: DEFAULT_CALCULATOR_PARAMS.fgtsRate,
      vacationRate: DEFAULT_CALCULATOR_PARAMS.vacationRate,
      thirteenthSalaryRate: DEFAULT_CALCULATOR_PARAMS.thirteenthSalaryRate,
      noticeRate: DEFAULT_CALCULATOR_PARAMS.noticeRate,
      sickLeaveRate: DEFAULT_CALCULATOR_PARAMS.sickLeaveRate,
      paternityLeaveRate: DEFAULT_CALCULATOR_PARAMS.paternityLeaveRate,
      terminationFineRate: DEFAULT_CALCULATOR_PARAMS.terminationFineRate,
      mealAllowance: DEFAULT_CALCULATOR_PARAMS.mealAllowance * ratio,
      healthPlan: DEFAULT_CALCULATOR_PARAMS.healthPlan * ratio,
      transportation: DEFAULT_CALCULATOR_PARAMS.transportation * ratio,
      financialEducation: DEFAULT_CALCULATOR_PARAMS.financialEducation * ratio,
    };

    try {
      const calculation = calculatePricing(input);
      return { result: calculation, errorMessage: null };
    } catch (error) {
      if (error instanceof CalculatorError) {
        return { result: null, errorMessage: error.message };
      }
      throw error;
    }
  }, [
    selectionCount,
    values.bySalary,
    values.bySaleHourlyRate,
    values.bySpread,
    values.salary,
    values.saleHourlyRate,
    values.spreadRate,
    values.hoursQuantity,
    values.hoursUnit,
    isHoursModuleEnabled,
  ]);

  const setValue = <Key extends keyof TeamCalculatorCardValues>(field: Key, nextValue: TeamCalculatorCardValues[Key]) => {
    setValues((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  useEffect(() => {
    onSnapshotChange?.(cardId, {
      id: cardId,
      title,
      values,
      result,
    });
  }, [cardId, onSnapshotChange, result, title, values, isHoursModuleEnabled]);

  useEffect(() => {
    if (!canSelectCollaborator) {
      setIsSelectingCollaborator(false);
    }
  }, [canSelectCollaborator]);

  const handleCollaboratorChange = (option: CollaboratorOption | null) => {
    setSelectedCollaborator(option);

    if (!option || typeof option.salary !== "number") {
      return;
    }

    const adjustedSalary =
      Math.round(toCltEquivalentSalary(option.salary, option.salaryType) * 100) / 100;
    const salaryString = adjustedSalary.toString();

    if (option.name) {
      setTitle(option.name);
    }

    setValues((prev) => {
      let next = {
        ...prev,
        salary: salaryString,
      };

      if (!prev.bySalary) {
        if (prev.bySaleHourlyRate && prev.bySpread) {
          next = {
            ...next,
            bySaleHourlyRate: false,
          };
        }
        next = {
          ...next,
          bySalary: true,
        };
      }

      return next;
    });

    setIsSelectingCollaborator(false);
  };

  const hoursUnitOptions = [
    { value: "hours", label: "Horas" },
    { value: "weeks", label: "Semanas" },
    { value: "months", label: "Meses" },
  ];

  const removeButtonClass =
    "inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-500 transition hover:border-gray-300 hover:text-gray-700";
  const collaboratorButtonClass =
    "inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50/70 px-3 py-1 text-xs font-semibold text-primary-700 shadow-sm transition hover:border-primary-300 hover:bg-primary-50";

  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-start gap-3 border-b border-gray-100 px-5 py-3">
        <div className="min-w-[220px] flex-1">
          {isSelectingCollaborator ? (
            <CollaboratorQuickSelect
              selected={selectedCollaborator}
              onChange={handleCollaboratorChange}
              onClose={() => setIsSelectingCollaborator(false)}
            />
          ) : (
            <input
              type="text"
              aria-label={`Nome do profissional ${index + 1}`}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onBlur={() => {
                if (title.trim() === "") {
                  setTitle(defaultTitle);
                }
              }}
              className="w-full rounded-lg border border-transparent bg-transparent text-sm font-semibold text-gray-900 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          {canSelectCollaborator && (
            <button
              type="button"
              onClick={() => setIsSelectingCollaborator((prev) => !prev)}
              className={collaboratorButtonClass}
            >
              {isSelectingCollaborator ? "Cancelar" : "Escolher colaborador"}
            </button>
          )}

          {canRemove && onRemove && (
            <button type="button" onClick={onRemove} className={removeButtonClass}>
              <XMarkIcon className="h-4 w-4" />
              Remover
            </button>
          )}
        </div>
      </header>

      <div className="space-y-4 px-5 py-5">

        <div className="grid gap-4 md:grid-cols-3">
          <DriverField
            label="Salário"
            checked={values.bySalary}
            disabled={driverDisabled.bySalary}
            value={values.salary}
            placeholder="Informe o salário"
            step="100"
            min="0"
            onCheckedChange={(checked) => setValue("bySalary", checked)}
            onValueChange={(next) => setValue("salary", next)}
            derivedContent={
              result && !values.bySalary ? (
                <div className="space-y-1">
                  <p className="text-base font-semibold text-gray-900">
                    {formatCurrency(result.salary)}
                  </p>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {formatCurrency(result.salaryPerHour, { suffix: "/hora" })}
                  </p>
                </div>
              ) : undefined
            }
          />

          <DriverField
            label="Valor hora de venda"
            checked={values.bySaleHourlyRate}
            disabled={driverDisabled.bySaleHourlyRate}
            value={values.saleHourlyRate}
            placeholder="Informe o valor hora"
            step="1"
            min="0"
            onCheckedChange={(checked) => setValue("bySaleHourlyRate", checked)}
            onValueChange={(next) => setValue("saleHourlyRate", next)}
            derivedContent={
              result && !values.bySaleHourlyRate ? (
                <div className="space-y-1">
                  <p className="text-base font-semibold text-gray-900">
                    {formatCurrency(result.saleValue, { suffix: "/mês" })}
                  </p>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {formatCurrency(result.saleHourlyRate, { suffix: "/hora" })}
                  </p>
                </div>
              ) : undefined
            }
          />

          <DriverField
            label="Spread"
            checked={values.bySpread}
            disabled={driverDisabled.bySpread}
            value={values.spreadRate}
            placeholder="Informe o spread"
            step="0.01"
            min="0"
            onCheckedChange={(checked) => setValue("bySpread", checked)}
            onValueChange={(next) => setValue("spreadRate", next)}
            derivedContent={
              result && !values.bySpread ? (
                <div className="space-y-1">
                  <p className="text-base font-semibold text-gray-900">
                    {formatPercent(result.spreadPercentage)}
                  </p>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {formatCurrency(result.spreadValue, { suffix: "/mês" })}
                  </p>
                </div>
              ) : undefined
            }
          />
        </div>

        {errorMessage && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {errorMessage}
          </div>
        )}

        {!errorMessage && !result && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
            Preencha os campos acima para visualizar o resultado.
          </div>
        )}

        {isHoursModuleEnabled && (
          <div className="rounded-2xl border border-gray-200 bg-gray-50/50 p-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantidade</label>
                <input
                  type="number"
                  value={values.hoursQuantity}
                  onChange={(e) => setValue("hoursQuantity", e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Unidade</label>
                <select
                  value={values.hoursUnit}
                  onChange={(e) =>
                    setValue("hoursUnit", e.target.value as "hours" | "weeks" | "months")
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  {hoursUnitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {values.hoursUnit !== "hours" && (
              <div className="mt-3 text-xs text-gray-500">
                Total calculado:{" "}
                <strong>
                  {calculateMonthlyHours(values.hoursQuantity, values.hoursUnit)} horas
                </strong>
              </div>
            )}
          </div>
        )}
      </div>
    </section >
  );
}

type CollaboratorOption = {
  id: string;
  name: string;
  salary?: number;
  salaryType?: string;
};

type DriverFieldProps = {
  label: string;
  checked: boolean;
  disabled: boolean;
  value: string;
  placeholder: string;
  step: string;
  min: string;
  onCheckedChange: (checked: boolean) => void;
  onValueChange: (next: string) => void;
};

function DriverField({
  label,
  checked,
  disabled,
  value,
  placeholder,
  step,
  min,
  onCheckedChange,
  onValueChange,
  derivedContent,
}: DriverFieldProps & { derivedContent?: ReactNode }) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checked && inputRef.current) {
      inputRef.current.focus();
    }
  }, [checked]);

  return (
    <div className="rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onCheckedChange(event.target.checked)}
            className={clsx(
              "h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600",
              disabled && "cursor-not-allowed bg-gray-100",
            )}
            disabled={disabled}
          />
          {label}
        </label>
      </div>

      {checked ? (
        <input
          ref={inputRef}
          type="number"
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          step={step}
          min={min}
          placeholder={placeholder}
          className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      ) : (
        <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/70 p-3 text-sm text-gray-600">
          {derivedContent ?? "O valor será estimado automaticamente quando os demais parâmetros estiverem preenchidos."}
        </div>
      )}
    </div>
  );
}

type CollaboratorQuickSelectProps = {
  selected: CollaboratorOption | null;
  onChange: (option: CollaboratorOption | null) => void;
  onClose: () => void;
};

type SalaryAwareCollaborator = Collaborator & {
  newSalaryEntryAmount?: number;
  newSalaryEntrySalaryType?: string;
  salaryHistorySalaries?: Array<{ amount?: number }>;
  salaryType?: string;
};

function CollaboratorQuickSelect({ selected, onChange, onClose }: CollaboratorQuickSelectProps) {
  const { data: collaborators = [], isLoading } = useCollaboratorsRealtimeQuery();
  const [query, setQuery] = useState("");

  const options = useMemo<CollaboratorOption[]>(() => {
    return (collaborators as SalaryAwareCollaborator[])
      .map((collaborator) => {
        const salary =
          typeof collaborator.newSalaryEntryAmount === "number"
            ? collaborator.newSalaryEntryAmount
            : Array.isArray(collaborator.salaryHistorySalaries) && collaborator.salaryHistorySalaries.length > 0
              ? collaborator.salaryHistorySalaries[0]?.amount
              : undefined;

        const salaryType = collaborator.newSalaryEntrySalaryType ?? collaborator.salaryType ?? undefined;

        return {
          id: collaborator.id ?? "",
          name: collaborator.name ?? "Sem nome",
          salary,
          salaryType,
        };
      })
      .filter((option) => option.id && option.name);
  }, [collaborators]);

  const normalizedQuery = normalizeString(query);
  const filteredOptions =
    normalizedQuery === ""
      ? options
      : options.filter((option) => normalizeString(option.name).includes(normalizedQuery));

  const resolvedSelected = selected
    ? options.find((option) => option.id === selected.id) ?? selected
    : null;

  const handleChange = (option: CollaboratorOption | null) => {
    onChange(option);
    setQuery("");
    if (option) {
      onClose();
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2">
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <span>Selecionar colaborador</span>
        <div className="flex items-center gap-2">
          {resolvedSelected && (
            <button
              type="button"
              onClick={() => handleChange(null)}
              className="text-primary-600 underline-offset-2 hover:underline"
            >
              Limpar
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 underline-offset-2 hover:underline"
          >
            Fechar
          </button>
        </div>
      </div>

      <Combobox value={resolvedSelected} onChange={handleChange}>
        <div className="relative mt-2">
          <Combobox.Input
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            displayValue={(option: CollaboratorOption | null) => option?.name ?? ""}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={isLoading ? "Carregando colaboradores..." : "Buscar por nome"}
            autoComplete="off"
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute z-10 mt-2 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-2 text-sm shadow-lg focus:outline-none">
              {filteredOptions.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500">Nenhum colaborador encontrado.</p>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        "cursor-pointer px-3 py-2",
                        active ? "bg-primary-50 text-primary-700" : "text-gray-700",
                      )
                    }
                  >
                    {({ selected: isSelected, active }) => (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={clsx("text-sm font-medium", isSelected ? "text-primary-700" : "text-gray-900")}>
                            {option.name}
                          </p>
                          {typeof option.salary === "number" && (
                            <p className="text-xs text-gray-500">
                              {formatCurrency(option.salary, { suffix: option.salaryType ? ` (${option.salaryType})` : undefined })}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <CheckIcon
                            className={clsx("h-5 w-5", active ? "text-primary-700" : "text-primary-600")}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
